/**
 * Rate Limiting and Authentication Security
 * Provides client-side rate limiting and brute force protection
 */

/**
 * Rate limiter for authentication attempts
 */
class RateLimiter {
  constructor() {
    this.attempts = new Map();
    this.blockedIPs = new Map();
    this.cleanupInterval = null;
    this.startCleanup();
  }

  /**
   * Configuration for rate limiting
   */
  static CONFIG = {
    // Login attempts
    LOGIN_MAX_ATTEMPTS: 5,
    LOGIN_WINDOW_MS: 15 * 60 * 1000, // 15 minutes
    LOGIN_BLOCK_DURATION_MS: 30 * 60 * 1000, // 30 minutes

    // Registration attempts
    REGISTER_MAX_ATTEMPTS: 3,
    REGISTER_WINDOW_MS: 60 * 60 * 1000, // 1 hour
    REGISTER_BLOCK_DURATION_MS: 60 * 60 * 1000, // 1 hour

    // Password reset attempts
    PASSWORD_RESET_MAX_ATTEMPTS: 3,
    PASSWORD_RESET_WINDOW_MS: 60 * 60 * 1000, // 1 hour
    PASSWORD_RESET_BLOCK_DURATION_MS: 60 * 60 * 1000, // 1 hour

    // OTP attempts
    OTP_MAX_ATTEMPTS: 3,
    OTP_WINDOW_MS: 15 * 60 * 1000, // 15 minutes
    OTP_BLOCK_DURATION_MS: 30 * 60 * 1000, // 30 minutes
  };

  /**
   * Checks if an action is rate limited
   */
  isRateLimited(identifier, action = 'login') {
    const key = `${identifier}:${action}`;
    const now = Date.now();

    // Check if blocked
    if (this.blockedIPs.has(key)) {
      const blockInfo = this.blockedIPs.get(key);
      if (now < blockInfo.blockedUntil) {
        return {
          blocked: true,
          remainingTime: blockInfo.blockedUntil - now,
          reason: `Too many ${action} attempts. Try again later.`
        };
      } else {
        // Block expired
        this.blockedIPs.delete(key);
      }
    }

    // Get configuration for action
    const config = this.getConfigForAction(action);

    // Check current attempts
    if (!this.attempts.has(key)) {
      this.attempts.set(key, []);
    }

    const attempts = this.attempts.get(key);
    const windowStart = now - config.windowMs;

    // Filter attempts within window
    const recentAttempts = attempts.filter(timestamp => timestamp > windowStart);
    this.attempts.set(key, recentAttempts);

    if (recentAttempts.length >= config.maxAttempts) {
      // Block the identifier
      this.blockedIPs.set(key, {
        blockedUntil: now + config.blockDurationMs,
        attempts: recentAttempts.length
      });

      return {
        blocked: true,
        remainingTime: config.blockDurationMs,
        reason: `Too many ${action} attempts. Account temporarily locked.`
      };
    }

    return { blocked: false };
  }

  /**
   * Records an attempt
   */
  recordAttempt(identifier, action = 'login') {
    const key = `${identifier}:${action}`;
    const now = Date.now();

    if (!this.attempts.has(key)) {
      this.attempts.set(key, []);
    }

    const attempts = this.attempts.get(key);
    attempts.push(now);
    this.attempts.set(key, attempts);
  }

  /**
   * Records a successful attempt (clears rate limiting)
   */
  recordSuccess(identifier, action = 'login') {
    const key = `${identifier}:${action}`;
    this.attempts.delete(key);
    this.blockedIPs.delete(key);
  }

  /**
   * Gets configuration for specific action
   */
  getConfigForAction(action) {
    switch (action) {
      case 'login':
        return {
          maxAttempts: RateLimiter.CONFIG.LOGIN_MAX_ATTEMPTS,
          windowMs: RateLimiter.CONFIG.LOGIN_WINDOW_MS,
          blockDurationMs: RateLimiter.CONFIG.LOGIN_BLOCK_DURATION_MS
        };
      case 'register':
        return {
          maxAttempts: RateLimiter.CONFIG.REGISTER_MAX_ATTEMPTS,
          windowMs: RateLimiter.CONFIG.REGISTER_WINDOW_MS,
          blockDurationMs: RateLimiter.CONFIG.REGISTER_BLOCK_DURATION_MS
        };
      case 'password-reset':
        return {
          maxAttempts: RateLimiter.CONFIG.PASSWORD_RESET_MAX_ATTEMPTS,
          windowMs: RateLimiter.CONFIG.PASSWORD_RESET_WINDOW_MS,
          blockDurationMs: RateLimiter.CONFIG.PASSWORD_RESET_BLOCK_DURATION_MS
        };
      case 'otp':
        return {
          maxAttempts: RateLimiter.CONFIG.OTP_MAX_ATTEMPTS,
          windowMs: RateLimiter.CONFIG.OTP_WINDOW_MS,
          blockDurationMs: RateLimiter.CONFIG.OTP_BLOCK_DURATION_MS
        };
      default:
        return {
          maxAttempts: 5,
          windowMs: 15 * 60 * 1000,
          blockDurationMs: 30 * 60 * 1000
        };
    }
  }

  /**
   * Starts cleanup interval
   */
  startCleanup() {
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, 5 * 60 * 1000); // Cleanup every 5 minutes
  }

  /**
   * Cleans up expired entries
   */
  cleanup() {
    const now = Date.now();

    // Clean up expired blocks
    for (const [key, blockInfo] of this.blockedIPs.entries()) {
      if (now >= blockInfo.blockedUntil) {
        this.blockedIPs.delete(key);
      }
    }

    // Clean up old attempts
    for (const [key, attempts] of this.attempts.entries()) {
      const filtered = attempts.filter(timestamp => now - timestamp < 24 * 60 * 60 * 1000); // Keep 24 hours
      if (filtered.length === 0) {
        this.attempts.delete(key);
      } else {
        this.attempts.set(key, filtered);
      }
    }
  }

  /**
   * Destroys the rate limiter
   */
  destroy() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    this.attempts.clear();
    this.blockedIPs.clear();
  }
}

/**
 * Session security manager
 */
class SessionSecurity {
  constructor() {
    this.sessionTimeout = 30 * 60 * 1000; // 30 minutes
    this.activityTimeout = null;
    this.warningTimeout = null;
    this.onSessionTimeout = null;
    this.onSessionWarning = null;
  }

  /**
   * Starts session monitoring
   */
  startSession(options = {}) {
    const {
      timeout = 30 * 60 * 1000, // 30 minutes
      onTimeout = null,
      onWarning = null
    } = options;

    this.sessionTimeout = timeout;
    this.onSessionTimeout = onTimeout;
    this.onSessionWarning = onWarning;

    this.resetActivityTimer();
    this.bindActivityEvents();
  }

  /**
   * Resets the activity timer
   */
  resetActivityTimer() {
    // Clear existing timers
    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout);
    }
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
    }

    // Set warning timer
    if (this.onSessionWarning) {
      this.warningTimeout = setTimeout(() => {
        this.onSessionWarning();
      }, this.sessionTimeout - 5 * 60 * 1000); // 5 minutes before timeout
    }

    // Set session timeout
    this.activityTimeout = setTimeout(() => {
      this.handleSessionTimeout();
    }, this.sessionTimeout);
  }

  /**
   * Binds activity events
   */
  bindActivityEvents() {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const resetTimer = () => {
      this.resetActivityTimer();
    };

    events.forEach(event => {
      document.addEventListener(event, resetTimer, true);
    });
  }

  /**
   * Handles session timeout
   */
  handleSessionTimeout() {
    if (this.onSessionTimeout) {
      this.onSessionTimeout();
    }
  }

  /**
   * Ends the session
   */
  endSession() {
    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout);
    }
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
    }
  }
}

/**
 * Password security utilities
 */
class PasswordSecurity {
  /**
   * Password strength requirements
   */
  static REQUIREMENTS = {
    minLength: 8,
    maxLength: 128,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true,
    preventCommonPasswords: true,
    preventUserInfo: true
  };

  /**
   * Common weak passwords to block
   */
  static COMMON_PASSWORDS = [
    'password', '123456', '123456789', 'qwerty', 'abc123',
    'password123', 'admin', 'letmein', 'welcome', 'monkey',
    '1234567890', 'password1', '123123', 'dragon', 'master'
  ];

  /**
   * Validates password strength
   */
  static validatePassword(password, userInfo = {}) {
    const errors = [];

    if (!password || typeof password !== 'string') {
      errors.push('Password is required');
      return { valid: false, errors, score: 0 };
    }

    // Length check
    if (password.length < this.REQUIREMENTS.minLength) {
      errors.push(`Password must be at least ${this.REQUIREMENTS.minLength} characters long`);
    }

    if (password.length > this.REQUIREMENTS.maxLength) {
      errors.push(`Password must be no more than ${this.REQUIREMENTS.maxLength} characters long`);
    }

    // Character requirements
    if (this.REQUIREMENTS.requireUppercase && !/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }

    if (this.REQUIREMENTS.requireLowercase && !/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }

    if (this.REQUIREMENTS.requireNumbers && !/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }

    if (this.REQUIREMENTS.requireSpecialChars && !/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }

    // Common password check
    if (this.REQUIREMENTS.preventCommonPasswords) {
      const lowerPassword = password.toLowerCase();
      if (this.COMMON_PASSWORDS.includes(lowerPassword)) {
        errors.push('Password is too common. Please choose a more unique password');
      }
    }

    // User info check
    if (this.REQUIREMENTS.preventUserInfo && userInfo) {
      const { email, firstName, lastName, phone } = userInfo;
      const lowerPassword = password.toLowerCase();

      if (email && lowerPassword.includes(email.split('@')[0].toLowerCase())) {
        errors.push('Password should not contain your email address');
      }

      if (firstName && lowerPassword.includes(firstName.toLowerCase())) {
        errors.push('Password should not contain your first name');
      }

      if (lastName && lowerPassword.includes(lastName.toLowerCase())) {
        errors.push('Password should not contain your last name');
      }

      if (phone && lowerPassword.includes(phone.replace(/\D/g, ''))) {
        errors.push('Password should not contain your phone number');
      }
    }

    // Calculate password score
    const score = this.calculatePasswordScore(password);

    return {
      valid: errors.length === 0,
      errors,
      score,
      strength: this.getPasswordStrength(score)
    };
  }

  /**
   * Calculates password strength score
   */
  static calculatePasswordScore(password) {
    let score = 0;

    // Length bonus
    score += Math.min(password.length * 2, 20);

    // Character variety bonus
    if (/[a-z]/.test(password)) score += 5;
    if (/[A-Z]/.test(password)) score += 5;
    if (/\d/.test(password)) score += 5;
    if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) score += 10;

    // Pattern penalties
    if (/(.)\1{2,}/.test(password)) score -= 10; // Repeated characters
    if (/123|abc|qwe|asd|zxc/i.test(password)) score -= 10; // Sequential patterns

    return Math.max(0, Math.min(100, score));
  }

  /**
   * Gets password strength label
   */
  static getPasswordStrength(score) {
    if (score < 30) return 'Weak';
    if (score < 60) return 'Fair';
    if (score < 80) return 'Good';
    return 'Strong';
  }

  /**
   * Generates secure password
   */
  static generateSecurePassword(length = 16) {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';

    const allChars = uppercase + lowercase + numbers + symbols;
    let password = '';

    // Ensure at least one character from each set
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];

    // Fill remaining length
    for (let i = 4; i < length; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }

    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  }
}

// Global rate limiter instance
const globalRateLimiter = new RateLimiter();

export {
  globalRateLimiter, PasswordSecurity, RateLimiter,
  SessionSecurity
};
